/**
 * Переходы по объекту CRM — как смарт-кнопки table3: объект (`dealID`,
 * `dogovorID`, `customerID`…) + id → главный переход и меню с группами.
 * Реестр `links.json` собирает `scripts/nav_import.py` из legacy a.reserv
 * (table3.js + link_config), руками не правится. Спека
 * `docs/specs/2026-09-25-nav-links-design.md`.
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import registry from "@/nav/links.json";

export type NavRef = { obj: string; id: number; pick?: string };

type Item = { title: string; url: string; sys: string; group: string | null };
type Obj = { label: string; items: Item[] };

const OBJECTS = registry.objects as Record<string, Obj>;
const SYSTEMS = registry.systems as Record<string, string>;

export function navUrl(item: Item, obj: string, id: number): string {
  return SYSTEMS[item.sys] + item.url.replaceAll(`{${obj}}`, String(id));
}

export function navObject(obj: string): Obj | undefined {
  return OBJECTS[obj];
}

export function NavMenu({ r }: { r: NavRef }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (ev: MouseEvent) => {
      if (box.current && !box.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const o = OBJECTS[r.obj];
  if (!o) return null;
  const main = o.items.find((i) => i.title === r.pick) ?? o.items[0]!;
  const groups = new Map<string, Item[]>();
  for (const i of o.items) {
    const g = i.group ?? "";
    groups.set(g, [...(groups.get(g) ?? []), i]);
  }
  return (
    <span ref={box} className="relative inline-flex items-baseline text-[11.5px]" data-annot={`переходы ${r.obj} ${r.id}`}>
      <a
        href={navUrl(main, r.obj, r.id)}
        target="_blank"
        rel="noreferrer"
        className="text-accent hover:underline"
        title={`${o.label} ${r.id}: ${main.title}`}
      >
        {r.pick ?? o.label} {r.id}
      </a>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-0.5 text-fg-faint hover:text-fg"
        aria-label={`все переходы: ${o.label} ${r.id}`}
        aria-expanded={open}
      >
        <ChevronDown size={11} aria-hidden />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 max-h-[60vh] w-64 overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-1 shadow-lg">
          {[...groups].map(([g, items]) => (
            <div key={g} className="py-0.5">
              {g && <div className="px-2 pt-1 text-[10.5px] uppercase tracking-wide text-fg-faint">{g}</div>}
              {items.map((i) => (
                <a
                  key={i.sys + i.url}
                  href={navUrl(i, r.obj, r.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-between gap-2 rounded px-2 py-0.5 text-fg hover:bg-bg-subtle"
                >
                  <span>{i.title}</span>
                  <span className="text-fg-faint">{i.sys === "a" ? "Резерв" : i.sys === "crm" ? "CRM" : "Б24"}</span>
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
